import React, { useState, useEffect } from 'react';
import { useFirebase } from '../Auth/FirebaseProvider';
import { statsService } from '../../services/dashboardService';
import {
  FaUsers,
  FaMoneyBillWave,
  FaShoppingCart,
  FaStar,
  FaUserCircle
} from 'react-icons/fa';
import { formatCurrency } from '../../utils/helpers';
import RevenueCalendar from './RevenueCalendar';
import {
  Box,
  Card,
  CardContent,
  Typography
} from '@mui/material';
import Loader from '../Common/Loader';

const StatCard = ({ icon: Icon, title, value, subValue, color }) => {
  return (
    <Card
      elevation={0}
      sx={{
        height: 120,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: color || 'primary.main',
          boxShadow: '0 2px 8px 0 rgb(0 0 0 / 0.1)',
        },
      }}
    >
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {/* Header compact */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: color ? `${color}20` : 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon style={{ fontSize: '1rem', color: color || '#059669' }} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              textAlign: 'right'
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Valeur principale */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.25rem',
              lineHeight: 1.1,
              color: 'text.primary',
              mb: subValue ? 0.5 : 0
            }}
          >
            {value}
          </Typography>

          {/* Sous-valeur */}
          {subValue && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                lineHeight: 1.2
              }}
            >
              {subValue}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};


// Composant principal Dashboard
function Dashboard() {
  const { foodTruckId, loading: contextLoading } = useFirebase();
  const [stats, setStats] = useState({
    totalClients: 0,
    signedClients: 0,
    monthlyOrders: 0,
    monthlyRevenue: 0,
    bestSellingItem: { name: '', quantity: 0 },
    bestClient: { name: '', orderCount: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [service, setService] = useState(null);

  useEffect(() => {
    if (!foodTruckId || contextLoading) {
      setService(null);
      return;
    }

    try {
      const statsServiceInstance = statsService.getInstance(foodTruckId);
      setService(statsServiceInstance);
      setError(null);
    } catch (error) {
      console.error("Error initializing stats service:", error);
      setError(error.message);
      setService(null);
    }
  }, [foodTruckId, contextLoading]);

  useEffect(() => {
    if (!service) return;

    let unsubscribeClients;
    let unsubscribeOrders;

    const setupListeners = async () => {
      try {
        setLoading(true);

        unsubscribeClients = service.listenClientsCount(
          (clients) => {
            setStats(prevStats => ({
              ...prevStats,
              totalClients: clients.length,
              signedClients: clients.filter(client => client.email).length
            }));
          },
          (error) => {
            console.error("Error fetching clients:", error);
            setError("Erreur lors du chargement des clients");
          }
        );

        unsubscribeOrders = service.listenMonthlyOrders(
          (orders) => {
            const monthlyRevenue = orders.reduce((sum, order) => sum + order.totalPrice, 0);

            const monthlyOrders = orders.length;
            const itemsSold = orders.flatMap(order => {
              // Get regular items
              const regularItems = order.items || [];
              
              // Get items from bundles
              const bundleItems = (order.bundles || []).flatMap(bundle => bundle.items || []);
              
              // Combine both sources
              return [...regularItems, ...bundleItems];
            });
            
            const itemCounts = itemsSold.reduce((acc, item) => {
              const quantity = typeof item.quantity === 'number' ? item.quantity : 1;
              acc[item.name] = (acc[item.name] || 0) + quantity;
              return acc;
            }, {});

            const bestSellingItem = Object.entries(itemCounts).reduce((best, [name, quantity]) =>
              quantity > best.quantity ? { name, quantity } : best
              , { name: '', quantity: 0 });

            const clientOrders = orders.reduce((acc, order) => {
              if (order.customerId && order.customerId !== 'Anonymous') {
                acc[order.customerId] = (acc[order.customerId] || 0) + 1;
              }
              return acc;
            }, {});

            const bestClient = Object.entries(clientOrders).reduce((best, [customerId, orderCount]) =>
              orderCount > best.orderCount ? { customerId, orderCount } : best
              , { customerId: '', orderCount: 0 });

            let clientName = 'Aucun client éligible';
            if (bestClient.customerId) {
              const clientOrder = orders.find(order => order.customerId === bestClient.customerId);
              clientName = clientOrder ? clientOrder.customerName : 'Client inconnu';
            }

            setStats(prevStats => ({
              ...prevStats,
              monthlyOrders,
              monthlyRevenue,
              bestSellingItem,
              bestClient: { name: clientName, orderCount: bestClient.orderCount }
            }));
          },
          (error) => {
            console.error("Error fetching orders:", error);
            setError("Erreur lors du chargement des commandes");
          }
        );

      } catch (error) {
        console.error("Error setting up listeners:", error);
        setError("Erreur lors de l'initialisation des écouteurs");
      } finally {
        setLoading(false);
      }
    };

    setupListeners();

    return () => {
      if (unsubscribeClients) unsubscribeClients();
      if (unsubscribeOrders) unsubscribeOrders();
    };
  }, [service]);

  if (contextLoading || loading) return <Loader />;
  if (!foodTruckId) return <Typography color="error">Aucun restaurant sélectionné</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const statCards = [
    {
      icon: FaUsers,
      title: "Clients",
      value: stats.totalClients,
      subValue: `${stats.signedClients} inscrits`,
      color: '#059669' // emerald-600
    },
    {
      icon: FaShoppingCart,
      title: "Commandes ce mois",
      value: stats.monthlyOrders,
      color: '#10b981' // emerald-500
    },
    {
      icon: FaMoneyBillWave,
      title: "Revenu mensuel",
      value: formatCurrency(stats.monthlyRevenue),
      color: '#f59e0b' // amber-500
    },
    {
      icon: FaStar,
      title: "Meilleure vente",
      value: stats.bestSellingItem.name,
      subValue: `Quantité : ${stats.bestSellingItem.quantity}`,
      color: '#3b82f6' // blue-500
    },
    {
      icon: FaUserCircle,
      title: "Meilleur client",
      value: stats.bestClient.name,
      subValue: `Commandes : ${stats.bestClient.orderCount}`,
      color: '#8b5cf6' // violet-500
    }
  ];

  return (
    <Box sx={{ p: 0 }}>
      {/* Header de la page */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 3,
        mb: 3
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary'
          }}
        >
          Tableau de bord
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary',
            mt: 0.5
          }}
        >
          Vue d'ensemble de votre activité
        </Typography>
      </Box>

      {/* Contenu principal */}
      <Box sx={{ px: 3 }}>
        {/* Cartes statistiques */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)', 
            md: 'repeat(5, 1fr)'
          },
          gap: 2,
          mb: 4
        }}>
          {statCards.map((card, index) => (
            <StatCard key={index} {...card} />
          ))}
        </Box>

        {/* Section calendrier */}
        <Box sx={{ 
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}>
          <RevenueCalendar />
        </Box>
      </Box>
    </Box>
  );
}

export default Dashboard;